import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import { Modal, Form, Radio, Button, message } from 'tea-component';
import { DownloadButton } from '@/pages/dashboard/components/Plugin/DownloadButton';
import { useNotificationOfDownloadTable } from '@/pages/dashboard/components/DownloadFormatModal';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import {
  useReportDownload,
  type TDownloadFormat,
} from '../../hooks/useReportDownload';
import type { IAnomalyReportListState } from '../../hooks/useAnomalyReportList';
import type { IDownloadFormatConfig } from '@/pages/dashboard/filterPageComponents/report/abnormalReportAllFilter';
import './index.scss';

type TInitialFilterState = 'loading' | 'error' | 'ready';

interface IAbnormalReportDownloadBtnProps {
  reportState?: IAnomalyReportListState;
  initialFilterState?: TInitialFilterState;
  className?: string;
  downloadFileNameEn?: string;
  downloadFileNameCn?: string;
  /**
   * 可下载的格式列表，顺序即渲染顺序，第一项为默认选中格式。
   * - 配置 1 项：直接下载，不弹窗
   * - 配置多项：弹窗让用户选择
   * 不配置时默认等同于 [{ format: 'html' }, { format: 'pdf' }]
   */
  downloadFormats?: IDownloadFormatConfig[];
}

const EMPTY_REPORT_STATE: IAnomalyReportListState = {
  loading: false,
  data: null,
  errorMsg: '',
};

/** 默认格式列表：HTML + PDF */
const DEFAULT_DOWNLOAD_FORMATS: IDownloadFormatConfig[] = [
  { format: 'html' },
  { format: 'pdf' },
];

/**
 * 异动日报下载按钮：格式选择弹窗 + 通知条
 * - 单格式：点击直接下载，无弹窗
 * - 多格式：弹窗选择后下载
 * 实际导出由 useReportDownload → @home/utils/htmlExport 完成
 */
const AbnormalReportDownloadBtn: React.FC<IAbnormalReportDownloadBtnProps> = ({
  reportState = EMPTY_REPORT_STATE,
  initialFilterState = 'loading',
  className,
  downloadFileNameEn,
  downloadFileNameCn,
  downloadFormats,
}) => {
  const { formatMessage, isEN } = useLocal();

  const resolvedFormats = downloadFormats?.length
    ? downloadFormats
    : DEFAULT_DOWNLOAD_FORMATS;

  const isSingleFormat = resolvedFormats.length === 1;
  const defaultFormat = resolvedFormats[0].format;

  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [format, setFormat] = useState<TDownloadFormat>(defaultFormat);

  const { canDownload, downloading, downloadReport } = useReportDownload({
    reportState,
    downloadFileNameEn,
    downloadFileNameCn,
  });

  const defaultDownloadName = formatMessage({ id: 'common_down_load' });
  const notificationName = isEN
    ? downloadFileNameEn ?? defaultDownloadName
    : downloadFileNameCn ?? defaultDownloadName;

  const { setNoticeStatus } = useNotificationOfDownloadTable(notificationName);

  const visible = initialFilterState === 'ready' && canDownload;

  /** 执行下载并更新通知状态 */
  const executeDownload = useCallback(
    async (targetFormat: TDownloadFormat): Promise<void> => {
      setNoticeStatus('loading');
      const success = await downloadReport(targetFormat);
      if (success) {
        setNoticeStatus('success');
      } else {
        setNoticeStatus('error');
        message.error({
          content: formatMessage({ id: 'common_download_failed' }) || 'Download failed',
        });
      }
    },
    [downloadReport, setNoticeStatus, formatMessage],
  );

  const handleClick = useCallback((): void => {
    if (isSingleFormat) {
      // 单格式：直接下载，不弹窗
      void executeDownload(defaultFormat);
    } else {
      // 多格式：重置为第一项后弹窗
      setFormat(defaultFormat);
      setModalVisible(true);
    }
  }, [isSingleFormat, defaultFormat, executeDownload]);

  const handleConfirm = useCallback(async (): Promise<void> => {
    setModalVisible(false);
    await executeDownload(format);
  }, [format, executeDownload]);

  const handleCancel = useCallback((): void => {
    setModalVisible(false);
  }, []);

  if (!visible) {
    return null;
  }

  const modalTitle =
    formatMessage({ id: 'download_format_modal_title' }) || 'Download Settings';
  const formatLabel =
    formatMessage({ id: 'dbd_download_format' }) || 'Format';
  const confirmText = formatMessage({ id: 'common_confirm' }) || 'Confirm';
  const cancelText = formatMessage({ id: 'common_cancel' }) || 'Cancel';

  return (
    <div className={classNames('dbd-abnormal-report-download-btn', className)}>
      <DownloadButton
        className="dbd-abnormal-report-download-btn__icon"
        placement="left"
        disabled={downloading}
        onClick={handleClick}
      />

      {!isSingleFormat && (
        <Modal
          visible={modalVisible}
          caption={modalTitle}
          onClose={handleCancel}
          size="s"
        >
          <Modal.Body>
            <Form>
              <Form.Item label={formatLabel}>
                <Radio.Group
                  value={format}
                  onChange={(val) => setFormat((val ?? defaultFormat) as TDownloadFormat)}
                >
                  {resolvedFormats.map((item) => {
                    const label = isEN
                      ? item.labelEn ?? item.format.toUpperCase()
                      : item.labelCn ?? item.format.toUpperCase();
                    return (
                      <Radio key={item.format} name={item.format}>
                        {label}
                      </Radio>
                    );
                  })}
                </Radio.Group>
              </Form.Item>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button
              type="primary"
              disabled={downloading}
              onClick={() => {
                void handleConfirm();
              }}
            >
              {confirmText}
            </Button>
            <Button type="weak" onClick={handleCancel}>
              {cancelText}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default AbnormalReportDownloadBtn;
